import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  InputNumber, 
  Select, 
  Button, 
  Row, 
  Col, 
  Typography, 
  Divider, 
  Space,
  Table,
  Tag,
  Statistic,
  Alert,
  Collapse
} from 'antd';
import { CalculatorOutlined, ThunderboltOutlined, ExperimentOutlined, AreaChartOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const ErosionCalculator = () => {
  const [form] = Form.useForm();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const rFactorOptions = [
    { label: '华南地区（600-800）', value: 700 },
    { label: '华东地区（500-700）', value: 600 },
    { label: '华中地区（400-600）', value: 500 },
    { label: '西南地区（300-500）', value: 400 },
    { label: '华北地区（200-400）', value: 300 },
    { label: '西北地区（100-300）', value: 200 },
    { label: '东北地区（150-350）', value: 250 },
  ];

  const soilTypeOptions = [
    { label: '砂土（0.05-0.10）', value: 0.075 },
    { label: '壤砂土（0.10-0.15）', value: 0.125 },
    { label: '砂壤土（0.15-0.20）', value: 0.175 },
    { label: '壤土（0.20-0.30）', value: 0.25 },
    { label: '粉砂壤土（0.30-0.40）', value: 0.35 },
    { label: '粉砂土（0.40-0.50）', value: 0.45 },
    { label: '砂质黏壤土（0.20-0.30）', value: 0.251 },
    { label: '黏壤土（0.25-0.35）', value: 0.30 },
    { label: '粉砂黏壤土（0.30-0.40）', value: 0.351 },
    { label: '砂质黏土（0.25-0.35）', value: 0.301 },
    { label: '粉砂黏土（0.30-0.40）', value: 0.352 },
    { label: '黏土（0.25-0.40）', value: 0.325 },
  ];

  const vegetationOptions = [
    { label: '裸地（1.0）', value: 1.0 },
    { label: '稀疏草地 <20%（0.45-0.60）', value: 0.525 },
    { label: '中等草地 20-40%（0.24-0.45）', value: 0.345 },
    { label: '茂密草地 40-60%（0.10-0.24）', value: 0.17 },
    { label: '稠密草地 >60%（0.01-0.10）', value: 0.055 },
    { label: '稀疏灌木 <20%（0.40-0.55）', value: 0.475 },
    { label: '中等灌木 20-40%（0.20-0.40）', value: 0.30 },
    { label: '茂密灌木 >40%（0.05-0.20）', value: 0.125 },
    { label: '稀疏林地 <20%（0.30-0.45）', value: 0.375 },
    { label: '中等林地 20-40%（0.10-0.30）', value: 0.20 },
    { label: '茂密林地 >40%（0.01-0.10）', value: 0.0551 },
    { label: '农耕地（0.15-0.45）', value: 0.30 },
    { label: '草田轮作（0.10-0.25）', value: 0.175 },
  ];

  const measureOptions = [
    { label: '无措施（1.0）', value: 1.0 },
    { label: '等高耕作（0.5-0.8）', value: 0.65 },
    { label: '等高带状种植（0.25-0.50）', value: 0.375 },
    { label: '梯田（0.01-0.10）', value: 0.055 },
    { label: '植被恢复（0.05-0.30）', value: 0.175 },
    { label: '工程护坡（0.01-0.20）', value: 0.105 },
    { label: '沉沙池（0.10-0.30）', value: 0.20 },
    { label: '排水沟（0.30-0.50）', value: 0.40 },
    { label: '挡土墙（0.01-0.10）', value: 0.0551 },
  ];

  const calculateLFactor = (slope, slopeLength) => {
    const slopePercent = slope * 100 / 57.296;
    let m;
    if (slopePercent <= 1) {
      m = 0.2;
    } else if (slopePercent <= 3) {
      m = 0.3;
    } else if (slopePercent <= 5) {
      m = 0.4;
    } else {
      m = 0.5;
    }
    return Math.pow(slopeLength / 22.1, m);
  };

  const calculateSFactor = (slope) => {
    const slopePercent = slope * 100 / 57.296;
    if (slope > 20) {
      const theta = slope * Math.PI / 180;
      return 16.8 * Math.sin(theta) - 0.5;
    } else {
      return (0.43 + 0.30 * slopePercent + 0.043 * Math.pow(slopePercent, 2)) / 6.613;
    }
  };

  const getRiskLevel = (erosionModulus) => {
    if (erosionModulus < 200) {
      return { level: '微度侵蚀', color: 'green' };
    } else if (erosionModulus < 2500) {
      return { level: '轻度侵蚀', color: 'blue' };
    } else if (erosionModulus < 5000) {
      return { level: '中度侵蚀', color: 'orange' };
    } else if (erosionModulus < 8000) {
      return { level: '强烈侵蚀', color: 'orange' };
    } else if (erosionModulus < 15000) {
      return { level: '极强烈侵蚀', color: 'red' };
    } else {
      return { level: '剧烈侵蚀', color: 'red' };
    }
  };

  const handleCalculate = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const R = values.rFactor;
      const K = values.kFactor;
      const slope = values.slope;
      const slopeLength = values.slopeLength;
      const C1 = values.cFactorBefore;
      const C2 = values.cFactorAfter;
      const P = values.pFactor;
      const area = values.area;
      const duration = values.duration;

      const L = calculateLFactor(slope, slopeLength);
      const S = calculateSFactor(slope);

      const A1 = R * K * L * S * C1 * 1;
      const A2 = R * K * L * S * C2 * P;

      const M1 = A1 * 100;
      const M2 = A2 * 100;

      const deltaA = (A2 - A1) * area;
      const deltaATotal = deltaA * duration;

      const riskBefore = getRiskLevel(M1);
      const riskAfter = getRiskLevel(M2);

      setResult({
        R, K, L, S, C1, C2, P,
        A1, A2, M1, M2,
        deltaA, deltaATotal,
        riskBefore, riskAfter,
        area, duration
      });
    } catch (error) {
      console.error('计算失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setResult(null);
  };

  const factorColumns = [
    {
      title: '因子',
      dataIndex: 'factor',
      key: 'factor',
      width: '30%',
    },
    {
      title: '符号',
      dataIndex: 'symbol',
      key: 'symbol',
      width: '15%',
    },
    {
      title: '数值',
      dataIndex: 'value',
      key: 'value',
      width: '25%',
      render: (value) => typeof value === 'number' ? value.toFixed(4) : value,
    },
    {
      title: '说明',
      dataIndex: 'description',
      key: 'description',
      width: '30%',
    },
  ];

  const factorData = result ? [
    {
      key: '1',
      factor: '降雨侵蚀力因子',
      symbol: 'R',
      value: result.R,
      description: 'MJ·mm/(hm²·h·a)',
    },
    {
      key: '2',
      factor: '土壤可蚀性因子',
      symbol: 'K',
      value: result.K,
      description: 't·hm²·h/(hm²·MJ·mm)',
    },
    {
      key: '3',
      factor: '坡长因子',
      symbol: 'L',
      value: result.L,
      description: '无量纲',
    },
    {
      key: '4',
      factor: '坡度因子',
      symbol: 'S',
      value: result.S,
      description: '无量纲',
    },
    {
      key: '5',
      factor: '扰动前植被因子',
      symbol: 'C₁',
      value: result.C1,
      description: '无量纲',
    },
    {
      key: '6',
      factor: '扰动后植被因子',
      symbol: 'C₂',
      value: result.C2,
      description: '无量纲',
    },
    {
      key: '7',
      factor: '水土保持措施因子',
      symbol: 'P',
      value: result.P,
      description: '无量纲',
    },
  ] : [];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px', color: '#1890ff' }}>
          <CalculatorOutlined /> 水土流失量预测计算器
        </Title>
        <Text type="secondary">
          基于通用土壤流失方程（USLE）预测项目区域的水土流失量
        </Text>
      </div>

      <Alert
        message="计算依据"
        description="本工具依据 GB 50433-2018《生产建设项目水土保持技术标准》、SL 190-2007《土壤侵蚀分类分级标准》、GB/T 50434-2018《生产建设项目水土流失防治标准》进行计算。"
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <Card title="输入参数" variant="borderless" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical">
              <Collapse 
                defaultActiveKey={['1', '2', '3', '4', '5']}
                items={[
                  {
                    key: '1',
                    label: <><ThunderboltOutlined /> 项目区基本信息</>,
                    children: (
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="项目面积（hm²）"
                            name="area"
                            rules={[{ required: true, message: '请输入项目面积' }]}
                          >
                            <InputNumber
                              min={0.1}
                              precision={2}
                              style={{ width: '100%' }}
                              placeholder="请输入项目面积"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="扰动时间（年）"
                            name="duration"
                            rules={[{ required: true, message: '请输入扰动时间' }]}
                          >
                            <InputNumber
                              min={1}
                              precision={1}
                              style={{ width: '100%' }}
                              placeholder="请输入扰动时间"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    )
                  },
                  {
                    key: '2',
                    label: <><ExperimentOutlined /> 地形信息</>,
                    children: (
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="坡度（°）"
                            name="slope"
                            rules={[{ required: true, message: '请输入坡度' }]}
                          >
                            <InputNumber
                              min={0}
                              max={90}
                              precision={2}
                              style={{ width: '100%' }}
                              placeholder="请输入坡度"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="坡长（m）"
                            name="slopeLength"
                            rules={[{ required: true, message: '请输入坡长' }]}
                          >
                            <InputNumber
                              min={1}
                              precision={2}
                              style={{ width: '100%' }}
                              placeholder="请输入坡长"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    )
                  },
                  {
                    key: '3',
                    label: <><AreaChartOutlined /> 土壤与植被信息</>,
                    children: (
                      <>
                        <Row gutter={[16, 16]}>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              label="降雨侵蚀力因子R"
                              name="rFactor"
                              rules={[{ required: true, message: '请选择R因子' }]}
                            >
                              <Select
                                placeholder="请选择R因子"
                                options={rFactorOptions}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              label="土壤可蚀性因子K"
                              name="kFactor"
                              rules={[{ required: true, message: '请选择K因子' }]}
                            >
                              <Select
                                placeholder="请选择K因子"
                                options={soilTypeOptions}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={[16, 16]}>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              label="扰动前植被因子C₁"
                              name="cFactorBefore"
                              rules={[{ required: true, message: '请选择扰动前C因子' }]}
                            >
                              <Select
                                placeholder="请选择扰动前C因子"
                                options={vegetationOptions}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              label="扰动后植被因子C₂"
                              name="cFactorAfter"
                              rules={[{ required: true, message: '请选择扰动后C因子' }]}
                            >
                              <Select
                                placeholder="请选择扰动后C因子"
                                options={vegetationOptions}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </>
                    )
                  },
                  {
                    key: '4',
                    label: '水土保持措施',
                    children: (
                      <Form.Item
                        label="水土保持措施因子P"
                        name="pFactor"
                        rules={[{ required: true, message: '请选择P因子' }]}
                      >
                        <Select
                          placeholder="请选择P因子"
                          options={measureOptions}
                        />
                      </Form.Item>
                    )
                  }
                ]}
              />

              <Divider />

              <Space>
                <Button 
                  type="primary" 
                  icon={<CalculatorOutlined />} 
                  onClick={handleCalculate}
                  loading={loading}
                  style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                >
                  开始计算
                </Button>
                <Button onClick={handleReset}>
                  重置
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          {result && (
            <>
              <Card title="计算结果" variant="borderless" style={{ marginBottom: '24px' }}>
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="扰动前侵蚀模数"
                      value={result.M1.toFixed(2)}
                      suffix="t/(km²·a)"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="扰动后侵蚀模数"
                      value={result.M2.toFixed(2)}
                      suffix="t/(km²·a)"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="年新增流失量"
                      value={result.deltaA.toFixed(2)}
                      suffix="t/a"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="总新增流失量"
                      value={result.deltaATotal.toFixed(2)}
                      suffix="t"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                </Row>

                <Divider />

                <div style={{ marginBottom: '16px' }}>
                  <Text strong>风险等级评估：</Text>
                </div>
                <Row gutter={[16, 16]}>
                  <Col xs={12}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <Text type="secondary">扰动前</Text>
                      </div>
                      <Tag color={result.riskBefore.color} style={{ fontSize: '16px', padding: '8px 16px' }}>
                        {result.riskBefore.level}
                      </Tag>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <Text type="secondary">扰动后</Text>
                      </div>
                      <Tag color={result.riskAfter.color} style={{ fontSize: '16px', padding: '8px 16px' }}>
                        {result.riskAfter.level}
                      </Tag>
                    </div>
                  </Col>
                </Row>
              </Card>

              <Card title="因子参数表" variant="borderless">
                <Table
                  columns={factorColumns}
                  dataSource={factorData}
                  pagination={false}
                  size="small"
                  bordered
                />
              </Card>
            </>
          )}

          {!result && (
            <Card variant="borderless">
              <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                <CalculatorOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />
                <div style={{ marginTop: '16px' }}>
                  <Text type="secondary">请输入参数并点击"开始计算"按钮</Text>
                </div>
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ErosionCalculator;
